// contrast.ts — WCAG relative-luminance contrast ratio for hex color pairs,
// the full theme-palette evaluator behind the CMS Design contrast card
// (a11y-03), and a dependency-free sRGB nudge that suggests an accessible
// replacement for the two brand colors.
function srgb(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// relativeLuminance is the WCAG relative luminance (0..1) of a hex color, or
// null if the hex is invalid.
export function relativeLuminance(hex: string): number | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  return 0.2126 * srgb(rgb[0]) + 0.7152 * srgb(rgb[1]) + 0.0722 * srgb(rgb[2]);
}

// contrastRatio returns the WCAG ratio (1..21) or null if either hex is invalid.
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

// bestOnColor is the best-achievable CTA text color for a brand fill: white or
// near-black (#111111), whichever contrasts more. Mirrors a11y-02's onColor.
function bestOn(brand: string): { ratio: number; on: string } | null {
  const onWhite = contrastRatio("#ffffff", brand);
  const onDark = contrastRatio("#111111", brand);
  if (onWhite === null || onDark === null) return null;
  return onWhite >= onDark ? { ratio: onWhite, on: "#ffffff" } : { ratio: onDark, on: "#111111" };
}

function toHex(rgb: [number, number, number]): string {
  return (
    "#" +
    rgb
      .map((c) =>
        Math.max(0, Math.min(255, Math.round(c)))
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}

// Interpolate every sRGB channel a fraction `t` (0..1) toward a single endpoint
// (0 = black, 255 = white). t=1 lands exactly on black/white, which always
// satisfies any contrast target — so the callers' loops are guaranteed to
// converge.
function lerpToward(
  rgb: [number, number, number],
  end: 0 | 255,
  t: number,
): [number, number, number] {
  return [rgb[0] + (end - rgb[0]) * t, rgb[1] + (end - rgb[1]) * t, rgb[2] + (end - rgb[2]) * t];
}

const STEP = 0.04; // 4% of the range per iteration
const MAX_STEPS = 40; // cap defensively; 40 * 0.04 = 1.6 ⇒ t clamps at 1 (pure end)

export type SuggestArgs =
  // "bestOn": raise the best achievable button-text contrast ON this brand to
  // `target` (used for cta_primary / cta_accent). Direction: push toward the
  // end whose contrast currently dominates bestOn — equivalent to the spec's
  // "luminance < ~0.18 darken, else lighten" but pinned to the true WCAG
  // crossover (~0.194, given the #111111 dark end) so a near-crossover gray is
  // never nudged the wrong way.
  | { brand: string; mode: "bestOn"; target: number; background?: undefined }
  // "against": raise contrast BETWEEN this brand and the background to `target`
  // (used for primary_background / accent_background). Direction: away from the
  // background's luminance — light bg ⇒ darken the brand, dark bg ⇒ lighten it,
  // chosen by which pure end contrasts more with the background.
  | { brand: string; mode: "against"; target: number; background: string };

// suggestBrandColor returns a nudged hex for one of the two brand colors whose
// resulting contrast clears `target`, or undefined if the brand hex is invalid.
export function suggestBrandColor(args: SuggestArgs): string | undefined {
  const rgb = parseHex(args.brand);
  if (!rgb) return undefined;

  let end: 0 | 255;
  let clears: (hex: string) => boolean;
  if (args.mode === "bestOn") {
    const b = bestOn(args.brand);
    if (!b) return undefined;
    // Toward white when the dark-end (#111111) contrast dominates; toward black
    // when the white contrast dominates.
    end = b.on === "#111111" ? 255 : 0;
    clears = (hex) => {
      const bb = bestOn(hex);
      return bb !== null && bb.ratio >= args.target;
    };
  } else {
    const bg = args.background;
    const towardBlack = (contrastRatio("#000000", bg) ?? 0) >= (contrastRatio("#ffffff", bg) ?? 0);
    end = towardBlack ? 0 : 255;
    clears = (hex) => {
      const r = contrastRatio(hex, bg);
      return r !== null && r >= args.target;
    };
  }

  for (let i = 1; i <= MAX_STEPS; i++) {
    const t = Math.min(1, i * STEP);
    const hex = toHex(lerpToward(rgb, end, t));
    if (clears(hex)) return hex;
  }
  // Defensive fallback: the pure endpoint (black/white) always clears.
  return toHex([end, end, end]);
}

export type ContrastSeverity = "error" | "warning";

export type ContrastPairKey =
  | "text_background"
  | "textmuted_background"
  | "text_surface"
  | "textmuted_surface"
  | "text_sectionalt"
  | "textmuted_sectionalt"
  | "cta_primary"
  | "cta_accent"
  | "primary_background"
  | "accent_background"
  | "primary_surface"
  | "accent_surface"
  | "primary_sectionalt"
  | "accent_sectionalt"
  | "accent_primary"
  | "surface_background";

export interface ContrastRow {
  pair: ContrastPairKey;
  // aHex is the foreground swatch (text / button-text / brand); bHex is the
  // surface behind it. Rendered as the two side-by-side swatches.
  aHex: string;
  bHex: string;
  ratio: number; // rounded to one decimal for display
  target: number; // 4.5 (neutral + cta) or 3.0 (brand ↔ surface)
  severity: ContrastSeverity;
  // suggestion is present ONLY for the brand pairs whose single color to change
  // is unambiguously primary/accent: the CTA pairs and every brand-vs-surface
  // pair (…_background / …_surface / …_sectionalt / accent_primary). The neutral
  // text pairs AND the surface_background elevation pair are informational —
  // which of the two colors to move is ambiguous, so no one-click fix is offered.
  suggestion?: string;
}

// Floor (not round) so a flagged row never displays a value that reads as
// passing — a failing 4.46 shows 4.4:1, not 4.5:1. Severity/tiering uses the
// raw ratio; this is display-only.
const floor1 = (r: number): number => Math.floor(r * 10) / 10;

// severityFor is only ever called for a FAILING pair (ratio < target): below
// 3.0 is unreadable (error), 3.0..target is fine for large/bold text only
// (warning). For target 3.0 the warning band is empty ⇒ every failure is error.
function severityFor(ratio: number): ContrastSeverity {
  return ratio < 3.0 ? "error" : "warning";
}

export interface ContrastColors {
  text: string;
  textMuted: string;
  background: string;
  surface: string;
  // sectionAlt is optional — when empty/falsy (derived via color-mix, not
  // client-computable) the section-alt checks are skipped entirely.
  sectionAlt?: string;
  primary: string;
  accent: string;
}

const NEUTRAL_TARGET = 4.5;
const CTA_TARGET = 4.5;
const BRAND_BG_TARGET = 3.0;

// evaluateContrast returns one row per FAILING pair across the whole theme
// palette. Order: neutral text legibility (4.5) first, then CTA button text
// (4.5), then brand-vs-surface (3.0: on background / card / alternating band /
// primary fill) with one-click suggestions, then the surface↔background
// elevation pair (3.0, informational). Neutral + elevation pairs are
// informational (no suggestion); the brand/CTA pairs offer a one-click fix.
export function evaluateContrast(c: ContrastColors): ContrastRow[] {
  const rows: ContrastRow[] = [];

  // ── Neutral text legibility (AA body 4.5:1; informational, no suggestion) ──
  const neutral: Array<[ContrastPairKey, string, string]> = [
    ["text_background", c.text, c.background],
    ["textmuted_background", c.textMuted, c.background],
    ["text_surface", c.text, c.surface],
    ["textmuted_surface", c.textMuted, c.surface],
  ];
  // Only check section-alt legibility when an explicit override is set; the
  // derived color-mix value is not client-computable here.
  if (c.sectionAlt) {
    neutral.push(["text_sectionalt", c.text, c.sectionAlt]);
    neutral.push(["textmuted_sectionalt", c.textMuted, c.sectionAlt]);
  }
  for (const [pair, a, b] of neutral) {
    const r = contrastRatio(a, b);
    if (r === null || r >= NEUTRAL_TARGET) continue;
    rows.push({
      pair,
      aHex: a,
      bHex: b,
      ratio: floor1(r),
      target: NEUTRAL_TARGET,
      severity: severityFor(r),
    });
  }

  // ── CTA button text on the brand fill (bestOn ≥ 4.5) ──
  const ctaPairs: Array<[ContrastPairKey, string]> = [
    ["cta_primary", c.primary],
    ["cta_accent", c.accent],
  ];
  for (const [pair, brand] of ctaPairs) {
    const b = bestOn(brand);
    if (b === null || b.ratio >= CTA_TARGET) continue;
    rows.push({
      pair,
      aHex: b.on, // the best achievable button-text color
      bHex: brand,
      ratio: floor1(b.ratio),
      target: CTA_TARGET,
      severity: severityFor(b.ratio),
      suggestion: suggestBrandColor({ brand, mode: "bestOn", target: CTA_TARGET }),
    });
  }

  // ── Brand color legible against the surface behind it (≥ 3.0): links,
  // outlines, icons and the a11y-11 focus ring drawn in the brand color on the
  // page background, on a card surface, on an alternating-section band, and the
  // accent used on a primary fill. Each carries a one-click Apply that nudges
  // the BRAND toward the pure end clearing the target against THAT background
  // (fg = brand token, bg = the surface behind it). ──
  const brandBgPairs: Array<[ContrastPairKey, string, string]> = [
    ["primary_background", c.primary, c.background],
    ["accent_background", c.accent, c.background],
    ["primary_surface", c.primary, c.surface],
    ["accent_surface", c.accent, c.surface],
  ];
  // Section-alt brand legibility: only when an explicit override is set (same
  // gate as the neutral section-alt pairs — the derived color-mix value is not
  // client-computable here).
  if (c.sectionAlt) {
    brandBgPairs.push(["primary_sectionalt", c.primary, c.sectionAlt]);
    brandBgPairs.push(["accent_sectionalt", c.accent, c.sectionAlt]);
  }
  // Accent used ON the primary fill (this direction only — primary-on-accent
  // does not occur on the rendered site).
  brandBgPairs.push(["accent_primary", c.accent, c.primary]);
  for (const [pair, brand, bg] of brandBgPairs) {
    const r = contrastRatio(brand, bg);
    if (r === null || r >= BRAND_BG_TARGET) continue;
    rows.push({
      pair,
      aHex: brand,
      bHex: bg,
      ratio: floor1(r),
      target: BRAND_BG_TARGET,
      severity: severityFor(r),
      suggestion: suggestBrandColor({
        brand,
        mode: "against",
        target: BRAND_BG_TARGET,
        background: bg,
      }),
    });
  }

  // ── Elevation: the card surface must be distinguishable from the page
  // background (≥ 3.0). Informational — which of the two neutrals to move is
  // ambiguous, so no one-click fix (fg = surface, bg = background). ──
  const surfaceBg = contrastRatio(c.surface, c.background);
  if (surfaceBg !== null && surfaceBg < BRAND_BG_TARGET) {
    rows.push({
      pair: "surface_background",
      aHex: c.surface,
      bHex: c.background,
      ratio: floor1(surfaceBg),
      target: BRAND_BG_TARGET,
      severity: severityFor(surfaceBg),
    });
  }

  return rows;
}
